import {
  BayLcgVcgTcgAndPairings,
  IBayPattern,
  IRowPattern,
  TLashingBridgePerRow,
  TLashingBridgeSide,
  getBayLevelRows,
  getDefaultLashingBridgeLcg,
  getLashingBridgeSlots,
} from "@tedivo/tedivo-bay-grid-pure";
import { BayLevelEnum, IBayLevelData, ISizeSummary } from "open-vessel-definition";
import {
  ILashingBridgeDraft,
  ILashingBridgeRow,
  TDraftKey,
  applyLashingBridgeDrafts,
  buildLashingBridgeRows,
  isValidHeight,
  isValidTiers,
  readLashingBridgeDrafts,
  summarizePerRow,
} from "./helpers/editLashingBridges/lashingBridgesEditModel";
import ovdJsonStore, { OVDChangesEnum } from "../../../../../app/stores/OVDJsonStore";

import { type EditDrawer } from "../../../../../components/common/EditDrawer";
import FieldsValuesStore from "../../../../../app/stores/FieldsValuesStore";
import { IntegratedDialogError } from "@tedivo/tedivo-ui";
import SlInput from "@shoelace-style/shoelace/dist/components/input/input";
import SlSwitch from "@shoelace-style/shoelace/dist/components/switch/switch";
import { createInputWithUnits } from "@tedivo/tedivo-form";
import { getTranslation } from "../../../../../app/i18/i18tn";
import globalUnits from "../../../../../app/units/globalUnits";
import goSquared from "../../../../../app/tracking/goSquared";
import { openPerRowExceptionsDialog } from "./helpers/editLashingBridges/createPerRowExceptionsDialog";

const SIDE_ENUM_KEY: Record<TLashingBridgeSide, string> = {
  aft: "enums:ForeAftEnum.AFT",
  fore: "enums:ForeAftEnum.FWD",
};

/**
 * Editor of the lashing bridges (OVD 1.3.0) of the Above bay-levels.
 * One table row per place a bridge can stand (`getLashingBridgeSlots`), plus the bridges stored
 * elsewhere, flagged. The per-row exceptions are edited in a modal.
 */
export function createLashingBridgesEdit({
  sizeSummary,
  bls,
  editDrawer,
  lcgVcgTcgAndPairings,
  usingAModal,
}: {
  sizeSummary: ISizeSummary;
  bls: Array<IBayLevelData>;
  editDrawer: EditDrawer;
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings | undefined;
  usingAModal: (isOpened: boolean) => void;
}): { node: HTMLElement; submitFunction: () => boolean } {
  goSquared.addEvent("Edit-OVD - Edit Lashing Bridges");

  const holder = document.createElement("div");
  const editingArea = document.createElement("section");
  editingArea.className = "editing-area";
  holder.appendChild(editingArea);

  const dataStore = new FieldsValuesStore();
  const drafts = readLashingBridgeDrafts(bls, sizeSummary.isoBays);
  const perRowByKey = new Map<TDraftKey, TLashingBridgePerRow>();
  drafts.forEach((d, key) => perRowByKey.set(key, d.perRow));

  const rows = buildLashingBridgeRows(
    lcgVcgTcgAndPairings ? getLashingBridgeSlots(lcgVcgTcgAndPairings) : undefined,
    drafts,
    sizeSummary.isoBays,
  );

  if (sizeSummary.maxAboveTier === undefined) {
    const notice = document.createElement("sl-alert");
    notice.variant = "warning";
    notice.open = true;
    notice.innerHTML = getTranslation("view:edit.lashingBridges.noAboveDeck");
    editingArea.appendChild(notice);
  }

  createEditingFields();
  dataStore.updateConsumers();

  dataStore.onChange = () => {
    editDrawer.dataIsDirty = true;
  };

  const submitFunction = () => {
    // Positions without a row keep what the file has
    const newDrafts = new Map<TDraftKey, ILashingBridgeDraft>(drafts);
    const invalidKeys: string[] = [];

    rows.forEach(({ key }) => {
      const draft: ILashingBridgeDraft = {
        on: dataStore.getValue(`${key}.on`) === 1,
        tiers: toNumber(dataStore.getValue(`${key}.tiers`)),
        height: toNumber(dataStore.getValue(`${key}.height`)),
        lcg: toNumber(dataStore.getValue(`${key}.lcg`)),
        perRow: perRowByKey.get(key) ?? {},
      };

      if (draft.on && (!isValidTiers(draft.tiers) || !isValidHeight(draft.height)))
        invalidKeys.push(key);

      newDrafts.set(key, draft);
    });

    if (invalidKeys.length) {
      new IntegratedDialogError(holder, true, getTranslation("general:common.close")).show(
        getTranslation("general:common.attention"),
        `${getTranslation("view:edit.lashingBridges.invalidValues")}<br />${invalidKeys
          .map(keyLabel)
          .join(", ")}`,
      );
      return false;
    }

    const changed = applyLashingBridgeDrafts(bls, sizeSummary.isoBays, newDrafts);

    goSquared.addEvent("Edit-OVD - Edit Lashing Bridges - Save");

    if (changed) ovdJsonStore.emitChange({ type: OVDChangesEnum.LASHING_BRIDGES });
    return true;
  };

  return { node: holder, submitFunction };

  function createEditingFields() {
    const table = document.createElement("table");
    const tHead = document.createElement("thead");
    const tBody = document.createElement("tbody");
    table.className = "tvd-table tvd-table-sticky";

    const cols = [
      "view:edit.lashingBridges.block",
      "view:edit.lashingBridges.side",
      "view:edit.lashingBridges.on",
      "view:edit.lashingBridges.lcg",
      "view:edit.lashingBridges.tiers",
      "view:edit.lashingBridges.height",
      "view:edit.lashingBridges.rowExceptions",
    ];

    const trh = document.createElement("tr");
    cols.forEach((colLabel) => {
      const th = document.createElement("th");
      const sp = document.createElement("span");
      sp.innerHTML = getTranslation(colLabel);
      sp.className = colLabel.replace(/[.|:]/gm, "-");
      th.appendChild(sp);
      trh.appendChild(th);
    });
    tHead.appendChild(trh);

    rows.forEach((row, idx) => {
      const aboveBl = bls.find((bl) => bl.level === BayLevelEnum.ABOVE && bl.isoBay === row.isoBay);
      const tr = createSideRow(row, getBayLevelRows(sizeSummary, aboveBl));

      // The block cell spans its consecutive rows
      if (idx === 0 || rows[idx - 1].block !== row.block) {
        let span = 1;
        while (rows[idx + span]?.block === row.block) span += 1;

        const tdBlock = document.createElement("td");
        tdBlock.innerHTML = row.block;
        tdBlock.rowSpan = span;
        tr.prepend(tdBlock);
      } else {
        tr.classList.add("lashing-bridge-second-side");
      }

      tBody.appendChild(tr);
    });

    table.appendChild(tHead);
    table.appendChild(tBody);
    editingArea.appendChild(table);
  }

  function createSideRow(row: ILashingBridgeRow, rowsInBay: IRowPattern[]): HTMLTableRowElement {
    const { key, isoBay, side } = row;
    const draft: ILashingBridgeDraft = drafts.get(key) ?? { on: false, perRow: {} };
    const tr = document.createElement("tr");

    const tdSide = document.createElement("td");
    tdSide.innerHTML = getTranslation(SIDE_ENUM_KEY[side]);
    if (!row.expected) {
      // A bridge stored on a position that isn't offered: shown so it can be turned off
      const badge = document.createElement("sl-badge");
      badge.variant = "warning";
      badge.style.marginLeft = "0.5em";
      badge.innerHTML = getTranslation("view:edit.lashingBridges.notExpected");
      badge.title = row.sharedWith
        ? getTranslation("view:edit.lashingBridges.notExpectedHelp", {
            other: keyLabel(row.sharedWith),
          })
        : `${isoBay} ${getTranslation(SIDE_ENUM_KEY[side])}`;
      tdSide.appendChild(badge);
    }
    tr.appendChild(tdSide);

    // On
    const tdOn = document.createElement("td");
    tdOn.className = "centered";
    const swOn = document.createElement("sl-switch");
    swOn.name = `${key}.on`;
    swOn.checked = draft.on;
    swOn.tabIndex = 0;
    dataStore.registerCheckbox(swOn);
    tdOn.appendChild(swOn);
    tr.appendChild(tdOn);

    // LCG (empty = the default position, also used by the drawings)
    const defaultLcg = lcgVcgTcgAndPairings
      ? getDefaultLashingBridgeLcg(lcgVcgTcgAndPairings, isoBay, side)
      : undefined;

    const tdLcg = document.createElement("td");
    const inpLcg = createInputWithUnits({
      name: `${key}.lcg`,
      value: draft.lcg,
      placeholder: defaultLcg,
      converter: globalUnits.lengthUnits,
      transponser: globalUnits.lcgTransposer,
      disabled: !draft.on,
    });
    inpLcg.title = getTranslation("view:edit.lashingBridges.lcgHelp");
    dataStore.registerInputField(inpLcg);
    tdLcg.appendChild(inpLcg);
    tr.appendChild(tdLcg);

    // Tiers
    const tdTiers = document.createElement("td");
    const inpTiers = document.createElement("sl-input");
    inpTiers.name = `${key}.tiers`;
    inpTiers.type = "number";
    inpTiers.size = "small";
    inpTiers.min = 0;
    inpTiers.step = 1;
    inpTiers.value = draft.tiers !== undefined ? String(draft.tiers) : "";
    inpTiers.disabled = !draft.on;
    dataStore.registerInputField(inpTiers);
    tdTiers.appendChild(inpTiers);
    tr.appendChild(tdTiers);

    // Height
    const tdHeight = document.createElement("td");
    const inpHeight = createInputWithUnits({
      name: `${key}.height`,
      value: draft.height,
      converter: globalUnits.lengthUnits,
      disabled: !draft.on,
    });
    inpHeight.title = getTranslation("view:edit.lashingBridges.heightHelp");
    dataStore.registerInputField(inpHeight);
    tdHeight.appendChild(inpHeight);
    tr.appendChild(tdHeight);

    // Row exceptions
    const tdExceptions = document.createElement("td");
    tdExceptions.className = "lashing-bridge-exceptions";
    const summary = document.createElement("span");
    summary.style.marginRight = "0.5em";
    const btnEdit = document.createElement("sl-button");
    btnEdit.size = "small";
    btnEdit.innerHTML = getTranslation("general:common.edit");
    btnEdit.disabled = !draft.on;
    tdExceptions.appendChild(summary);
    tdExceptions.appendChild(btnEdit);
    tr.appendChild(tdExceptions);

    const refreshSummary = () => {
      const { text, outside } = summarizePerRow(
        perRowByKey.get(key) ?? {},
        rowsInBay,
        globalUnits.lengthUnits.fromValueToFormattedDisplay,
      );
      summary.textContent = text;
      summary.title = outside ? getTranslation("view:edit.lashingBridges.outsideBay") : "";
    };
    refreshSummary();

    btnEdit.addEventListener("click", () => {
      openPerRowExceptionsDialog({
        parent: editingArea,
        isoBay,
        side,
        rows: rowsInBay,
        perRow: perRowByKey.get(key) ?? {},
        defaults: {
          tiers: toNumber(dataStore.getValue(`${key}.tiers`)),
          height: toNumber(dataStore.getValue(`${key}.height`)),
        },
        usingAModal,
        onApply: (perRow) => {
          perRowByKey.set(key, perRow);
          refreshSummary();
          editDrawer.dataIsDirty = true;
        },
      });
    });

    connectSwitchAndFields(key, swOn, [inpLcg, inpTiers, inpHeight, btnEdit], inpTiers);

    return tr;
  }

  function connectSwitchAndFields(
    key: TDraftKey,
    sw: SlSwitch,
    fields: Array<{ disabled: boolean }>,
    inpTiers: SlInput,
  ) {
    sw.addEventListener(
      "sl-change",
      () => {
        fields.forEach((f) => (f.disabled = !sw.checked));
        if (sw.checked && inpTiers.value === "") dataStore.setValue(`${key}.tiers`, "1");
      },
      false,
    );

    sw.addEventListener(
      "keydown",
      (ev: KeyboardEvent) => {
        if (sw.disabled || ev.key !== " ") return;
        ev.preventDefault();
        sw.checked = !sw.checked;
        sw.dispatchEvent(new Event("sl-change"));
      },
      false,
    );
  }

  function keyLabel(key: string): string {
    const [isoBay, side] = key.split(".") as [IBayPattern, TLashingBridgeSide];
    return `${isoBay} ${getTranslation(SIDE_ENUM_KEY[side])}`;
  }
}

function toNumber(v: string | number | undefined): number | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}
